import { useNavigate } from 'react-router-dom'
import { PageFull } from '@/presentation/components/PageFull'

export default function PrivacyPolicy() {
  const navigate = useNavigate()

  const handleClose = () => {
    navigate('/login')
  }

  return (
    <PageFull className='relative'>
      <button
        onClick={handleClose}
        className='sticky right-6 top-6 z-50 ml-auto mr-6 mt-6 flex h-10 w-10 items-center justify-center rounded-full bg-white shadow-lg hover:shadow-xl transition-all hover:scale-110'
        data-testid='close-button'
        aria-label='Close'
      >
        <svg
          width='20'
          height='20'
          viewBox='0 0 16 16'
          fill='none'
          xmlns='http://www.w3.org/2000/svg'
        >
          <path
            d='M12 4L4 12M4 4l8 8'
            stroke='#5c17f2'
            strokeWidth='2'
            strokeLinecap='round'
            strokeLinejoin='round'
          />
        </svg>
      </button>

      <div className='px-6 pb-8' data-testid='privacy-policy-page'>
        <h1 className='mb-6 text-2xl font-bold text-[#5c17f2]'>
          Privacy Policy
        </h1>

        <div className='space-y-6 text-sm leading-relaxed text-gray-700'>
          <section>
            <p
              className='mb-4 text-xs text-gray-500'
              data-testid='last-updated'
            >
              Last Updated: January 4, 2026
            </p>
            <p>
              This Privacy Policy explains how we collect, use, disclose, and
              protect your personal information when you participate in our
              Affiliate Partner Program. We are committed to protecting your
              privacy and complying with applicable data protection laws.
            </p>
          </section>

          <section>
            <h2
              className='mb-3 text-lg font-semibold text-[#5c17f2]'
              data-testid='section-information-collect'
            >
              1. Information We Collect
            </h2>
            <h3 className='mb-2 font-semibold'>1.1 Information You Provide</h3>
            <p className='mb-2'>
              When you register as an affiliate, we collect:
            </p>
            <ul className='ml-6 list-disc space-y-1'>
              <li>
                Account information: name, email address, phone number, account
                credentials
              </li>
              <li>
                Payment information: payment method details, withdrawal account
                information
              </li>
              <li>
                Business information: company name, tax identification numbers
                (if applicable)
              </li>
              <li>Communication data: messages, support inquiries, feedback</li>
            </ul>

            <h3 className='mb-2 mt-4 font-semibold'>
              1.2 Automatically Collected Information
            </h3>
            <p className='mb-2'>
              We automatically collect technical and usage data:
            </p>
            <ul className='ml-6 list-disc space-y-1'>
              <li>
                Device information: device ID, operating system, browser type,
                IP address
              </li>
              <li>
                Usage data: login times, pages viewed, referral link
                performance, click data
              </li>
              <li>
                Tracking data: cookies, web beacons, and similar technologies to
                track referrals
              </li>
              <li>
                Performance metrics: conversion rates, earnings statistics,
                traffic sources
              </li>
            </ul>

            <h3 className='mb-2 mt-4 font-semibold'>1.3 Referral Data</h3>
            <p>
              We collect information about users referred through your affiliate
              links, including registration timestamps, user activity, and
              conversion data necessary for commission calculation.
            </p>
          </section>

          <section>
            <h2
              className='mb-3 text-lg font-semibold text-[#5c17f2]'
              data-testid='section-how-we-use'
            >
              2. How We Use Your Information
            </h2>
            <p className='mb-2'>We use your personal information to:</p>
            <ul className='ml-6 list-disc space-y-1'>
              <li>Operate and manage the affiliate program</li>
              <li>Track referrals and calculate commissions accurately</li>
              <li>Process payments and maintain financial records</li>
              <li>Communicate program updates, promotional opportunities</li>
              <li>Provide customer support and respond to inquiries</li>
              <li>
                Prevent fraud, detect security threats, and enforce our terms
              </li>
              <li>Analyze program performance and improve our services</li>
              <li>Comply with legal obligations and regulatory requirements</li>
            </ul>
          </section>

          <section>
            <h2
              className='mb-3 text-lg font-semibold text-[#5c17f2]'
              data-testid='section-legal-basis'
            >
              3. Legal Basis for Processing (GDPR)
            </h2>
            <p className='mb-2'>
              For users in the European Economic Area, we process your data
              based on:
            </p>
            <ul className='ml-6 list-disc space-y-1'>
              <li>
                <strong>Contract Performance:</strong> Processing necessary to
                fulfill our affiliate agreement
              </li>
              <li>
                <strong>Legitimate Interests:</strong> Fraud prevention,
                security, and business analytics
              </li>
              <li>
                <strong>Legal Compliance:</strong> Tax reporting, anti-money
                laundering requirements
              </li>
              <li>
                <strong>Consent:</strong> Marketing communications (you may
                withdraw consent anytime)
              </li>
            </ul>
          </section>

          <section>
            <h2
              className='mb-3 text-lg font-semibold text-[#5c17f2]'
              data-testid='section-sharing'
            >
              4. Information Sharing and Disclosure
            </h2>
            <p className='mb-2'>We may share your information with:</p>

            <h3 className='mb-2 mt-4 font-semibold'>4.1 Service Providers</h3>
            <p className='mb-2'>Third-party vendors who assist with:</p>
            <ul className='ml-6 list-disc space-y-1'>
              <li>Payment processing and financial services</li>
              <li>Cloud hosting and data storage</li>
              <li>Analytics and tracking platforms</li>
              <li>Customer support tools</li>
            </ul>

            <h3 className='mb-2 mt-4 font-semibold'>4.2 Legal Requirements</h3>
            <p>
              We disclose information when required by law, court order, or to
              protect our rights, property, or safety.
            </p>

            <h3 className='mb-2 mt-4 font-semibold'>4.3 Business Transfers</h3>
            <p>
              In the event of merger, acquisition, or asset sale, your
              information may be transferred to the acquiring entity.
            </p>

            <p className='mt-4'>
              We do NOT sell your personal information to third parties for
              marketing purposes.
            </p>
          </section>

          <section>
            <h2
              className='mb-3 text-lg font-semibold text-[#5c17f2]'
              data-testid='section-cookies'
            >
              5. Cookies and Tracking Technologies
            </h2>
            <p className='mb-2'>We use cookies and similar technologies for:</p>
            <ul className='ml-6 list-disc space-y-1'>
              <li>
                <strong>Essential Cookies:</strong> Required for login and
                security features
              </li>
              <li>
                <strong>Affiliate Tracking:</strong> Tracking referrals with
                30-day attribution window
              </li>
              <li>
                <strong>Analytics:</strong> Understanding usage patterns and
                program performance
              </li>
              <li>
                <strong>Preference Cookies:</strong> Remembering your settings
                and preferences
              </li>
            </ul>
            <p className='mt-3'>
              You can control cookies through your browser settings, but
              disabling essential cookies may affect program functionality.
            </p>
          </section>

          <section>
            <h2
              className='mb-3 text-lg font-semibold text-[#5c17f2]'
              data-testid='section-security'
            >
              6. Data Security
            </h2>
            <p className='mb-2'>
              We implement industry-standard security measures:
            </p>
            <ul className='ml-6 list-disc space-y-1'>
              <li>Encryption of data in transit (TLS/SSL) and at rest</li>
              <li>Secure authentication and access controls</li>
              <li>Regular security audits and vulnerability assessments</li>
              <li>Employee training on data protection practices</li>
            </ul>
            <p className='mt-3'>
              However, no system is completely secure. You are responsible for
              maintaining the confidentiality of your account credentials.
            </p>
          </section>

          <section>
            <h2
              className='mb-3 text-lg font-semibold text-[#5c17f2]'
              data-testid='section-retention'
            >
              7. Data Retention
            </h2>
            <p className='mb-2'>We retain your information:</p>
            <ul className='ml-6 list-disc space-y-1'>
              <li>
                For the duration of your participation in the affiliate program
              </li>
              <li>
                Up to 7 years after account closure for financial records and
                tax compliance
              </li>
              <li>Longer if required by law or necessary for legal claims</li>
            </ul>
            <p className='mt-3'>
              Anonymized analytics data may be retained indefinitely.
            </p>
          </section>

          <section>
            <h2
              className='mb-3 text-lg font-semibold text-[#5c17f2]'
              data-testid='section-rights'
            >
              8. Your Privacy Rights
            </h2>
            <p className='mb-2'>Depending on your location, you may have:</p>

            <h3 className='mb-2 mt-4 font-semibold'>
              8.1 Access and Portability
            </h3>
            <p>
              Request a copy of your personal data in a commonly used,
              machine-readable format.
            </p>

            <h3 className='mb-2 mt-4 font-semibold'>8.2 Correction</h3>
            <p>
              Update or correct inaccurate information through your account
              dashboard.
            </p>

            <h3 className='mb-2 mt-4 font-semibold'>8.3 Deletion</h3>
            <p>
              Request deletion of your data, subject to legal retention
              requirements.
            </p>

            <h3 className='mb-2 mt-4 font-semibold'>8.4 Objection</h3>
            <p>
              Object to processing based on legitimate interests (may affect
              program participation).
            </p>

            <h3 className='mb-2 mt-4 font-semibold'>8.5 Withdraw Consent</h3>
            <p>
              Withdraw consent for marketing communications or optional data
              processing.
            </p>

            <p className='mt-4'>
              To exercise these rights, contact us through your partner
              dashboard or our support email.
            </p>
          </section>

          <section>
            <h2 className='mb-3 text-lg font-semibold text-[#5c17f2]'>
              9. International Data Transfers
            </h2>
            <p>
              Your information may be transferred to and processed in countries
              outside your residence. We ensure adequate safeguards through:
            </p>
            <ul className='ml-6 list-disc space-y-1'>
              <li>
                Standard Contractual Clauses approved by the EU Commission
              </li>
              <li>Data Processing Agreements with third-party processors</li>
              <li>
                Compliance with Privacy Shield principles (where applicable)
              </li>
            </ul>
          </section>

          <section>
            <h2 className='mb-3 text-lg font-semibold text-[#5c17f2]'>
              10. California Privacy Rights (CCPA)
            </h2>
            <p className='mb-2'>California residents have additional rights:</p>
            <ul className='ml-6 list-disc space-y-1'>
              <li>
                Right to know what personal information is collected, used,
                shared
              </li>
              <li>Right to delete personal information (with exceptions)</li>
              <li>Right to opt-out of sale of personal information</li>
              <li>Right to non-discrimination for exercising privacy rights</li>
            </ul>
            <p className='mt-3'>
              We do not sell personal information as defined under CCPA.
            </p>
          </section>

          <section>
            <h2 className='mb-3 text-lg font-semibold text-[#5c17f2]'>
              11. Children's Privacy
            </h2>
            <p>
              Our affiliate program is not intended for individuals under 18
              years of age. We do not knowingly collect information from
              children. If we discover such collection, we will delete the data
              immediately.
            </p>
          </section>

          <section>
            <h2 className='mb-3 text-lg font-semibold text-[#5c17f2]'>
              12. Third-Party Links
            </h2>
            <p>
              Our platform may contain links to third-party websites. We are not
              responsible for the privacy practices of these external sites.
              Review their privacy policies before providing any information.
            </p>
          </section>

          <section>
            <h2 className='mb-3 text-lg font-semibold text-[#5c17f2]'>
              13. Changes to This Privacy Policy
            </h2>
            <p>
              We may update this Privacy Policy periodically. Material changes
              will be communicated via email or dashboard notification 30 days
              before taking effect. The "Last Updated" date reflects the most
              recent revision.
            </p>
          </section>

          <section>
            <h2 className='mb-3 text-lg font-semibold text-[#5c17f2]'>
              14. Contact Us
            </h2>
            <p className='mb-2'>For privacy-related questions or concerns:</p>
            <ul className='ml-6 list-disc space-y-1'>
              <li>
                Contact our Data Protection Officer through the partner
                dashboard
              </li>
              <li>Submit a privacy request via the support ticket system</li>
              <li>
                For GDPR-related inquiries, you may contact your local data
                protection authority
              </li>
            </ul>
          </section>

          <section>
            <h2 className='mb-3 text-lg font-semibold text-[#5c17f2]'>
              15. Supervisory Authority
            </h2>
            <p>
              If you are located in the EEA and believe we have not adequately
              addressed your privacy concerns, you have the right to lodge a
              complaint with your local data protection supervisory authority.
            </p>
          </section>
        </div>
      </div>
    </PageFull>
  )
}
